import { useAppSelector } from "@/store/store";

import { useAuth } from "@/hooks/useAuth";
import { Apartment, PeriodName } from "@/models";
import { useMemo } from "react"; // Add this import
import { useActiveApartments, useAllApartments, useGetApartmentsQuery } from "@/store/slices/apartments";
import {
    useGetApartmentOwnersQuery,
    useGetExpenseCategoriesQuery,
    useGetGroupsQuery,
    useGetMunicipalitySectionsQuery,
    useGetOwnerGroupsQuery,
    useGetYearsInOperationQuery,
} from "@/store/booking-management-api";

export const useAppData = () => {
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;

    // Get data from RTK Query
    const { data: owners } = useGetApartmentOwnersQuery(isAdmin);
    const { data: apartments } = useGetApartmentsQuery(isAdmin);
    // `groups` (apartment groups) is admin-only: getGroups always targets
    // `admin/groups`. For a non-admin owner that 403s; in the unauthenticated
    // demo it resolves to /api/demo/admin/groups → NoRoute → index.html, which
    // throws in transformResponse and breaks the page. Skip it unless admin,
    // mirroring AppDataProvider.
    const { data: groups } = useGetGroupsQuery(undefined, { skip: !isAdmin });
    const { data: expenseCategories } = useGetExpenseCategoriesQuery();
    const { data: years } = useGetYearsInOperationQuery(isAdmin);
    const { data: ownerGroups } = useGetOwnerGroupsQuery();
    const { data: municipalitySections } = useGetMunicipalitySectionsQuery();

    const { selectedYear, selectedPeriod, selectedOwner, selectedGroup, selectedApartment, aggregateMethod } = useAppSelector(
        (state) => state.userSettings,
    );
    const apartmentDetails = useAllApartments();
    const activeApartmentDetails = useActiveApartments();

    // Memoize all maps
    const ownerIdMap = useMemo(() => new Map(owners?.map((owner) => [owner.id, owner]) || []), [owners]);

    const ownerApartmentsMap = useMemo(() => {
        if (apartmentDetails.length === 0) {
            return new Map();
        }
        const apartmentsByOwner = new Map<number, { apartmentId: number; percentage: number }[]>();
        apartmentDetails.forEach((apartment) => {
            if (apartment.ownerIds.length === 0) {
                return;
            }
            apartment.ownerIds.forEach((ownerId) => {
                if (!apartmentsByOwner.has(ownerId)) {
                    apartmentsByOwner.set(ownerId, []);
                }
                apartmentsByOwner.get(ownerId)?.push({
                    apartmentId: apartment.id,
                    percentage: 100 / apartment.ownerIds.length,
                });
            });
        });

        return apartmentsByOwner;
    }, [apartmentDetails]);

    const apartmentIDMap: Map<number, Apartment> = useMemo(() => {
        const ret = new Map<number, Apartment>();
        apartmentDetails.forEach((apartment) => {
            ret.set(apartment.id, apartment);
        });
        return ret;
    }, [apartmentDetails]);

    const apartmentDetailsMap = useMemo(
        () => new Map(apartmentDetails?.map((apartment) => [apartment.id, apartment]) || []),
        [apartmentDetails],
    );

    const groupIdMap = useMemo(() => new Map(groups?.map((group) => [group.id, group]) || []), [groups]);

    const expenseCategoriesMap = useMemo(() => new Map(expenseCategories?.map((cat) => [cat.id, cat]) || []), [expenseCategories]);

    // Memoize options arrays
    const yearOptions = useMemo(
        () =>
            (years || []).map((year) => ({
                value: year.toString(),
                label: year.toString(),
            })),
        [years],
    );

    const numberOfBedroomsOptions = useMemo(() => {
        return [
            { value: "0", label: "Any" },
            { value: "1", label: "1" },
            { value: "2", label: "2+" },
        ];
    }, []);

    const activeMunicipalitySectionOptions = useMemo(() => {
        const activeMunicipalitySections = new Set<string>(activeApartmentDetails.map((apartment) => apartment.municipalitySectionId.toString()));
        return [
            { value: "0", label: "Any" },
            ...Array.from(activeMunicipalitySections).map((sectionId) => {
                const section = municipalitySections?.find((s) => s.id.toString() === sectionId);

                return {
                    value: sectionId,
                    label: section?.name || "",
                };
            }),
        ].sort((a, b) => a.value.localeCompare(b.value));
    }, [activeApartmentDetails, municipalitySections]);

    const activeOwnerGroupOptions = useMemo(() => {
        const activeOwnerGroups = new Set<string>(activeApartmentDetails.map((apartment) => apartment.ownerGroupId.toString()));
        return [
            { value: "0", label: "All" },
            ...Array.from(activeOwnerGroups).map((ownerGroupId) => {
                const ownerGroup = ownerGroups?.find((g) => g.id.toString() === ownerGroupId);

                return {
                    value: ownerGroupId,
                    label: ownerGroup?.name || "",
                };
            }),
        ].sort((a, b) => a.value.localeCompare(b.value));
    }, [activeApartmentDetails, ownerGroups]);

    const ownerOptions = useMemo(
        () =>
            ((owners ? [...owners] : []).sort((a, b) => a.fullName.localeCompare(b.fullName)) || [])
                .sort((a, b) => a.fullName.localeCompare(b.fullName))
                .map((owner) => ({
                    value: owner.id.toString(),
                    label: owner.fullName,
                })),
        [owners],
    );

    const groupOptions = useMemo(
        () => [
            { value: "0", label: "All" },
            ...(groups || []).map((group) => ({
                value: group.id.toString(),
                label: group.name,
            })),
        ],
        [groups],
    );

    const periodOptions = useMemo(
        () =>
            Object.keys(PeriodName).map((period) => ({
                value: period,
                label: PeriodName[period as keyof typeof PeriodName],
            })),
        [], // Empty dependency array as this never changes
    );

    const aggregateMethodOptions = useMemo(
        () => [
            { value: "sum", label: "Sum" },
            { value: "avg", label: "Average" },
        ],
        [],
    ); // Empty dependency array as this never changes

    const municipalitySectionOptions = useMemo(
        () =>
            (municipalitySections || []).map((section) => ({
                value: section.id.toString(),
                label: section.name,
            })),
        [municipalitySections],
    );

    const ownerGroupOptions = useMemo(
        () =>
            (ownerGroups || []).map((group) => ({
                value: group.id.toString(),
                label: group.name,
            })),
        [ownerGroups],
    );

    return {
        // Data
        owners: owners || [],
        apartments: apartments || [],
        groups: groups || [],
        expenseCategories: expenseCategories || [],
        years: years || [],
        municipalitySections: municipalitySections || [],
        ownerGroups: ownerGroups || [],

        // Maps
        ownerIdMap,
        groupIdMap,
        apartmentDetailsMap,
        ownerApartmentsMap,
        expenseCategoriesMap,
        apartmentIDMap,

        // Options for select components
        yearOptions,
        ownerOptions,
        groupOptions,
        aggregateMethodOptions,
        periodOptions,
        municipalitySectionOptions,
        ownerGroupOptions,
        numberOfBedroomsOptions,
        activeMunicipalitySectionOptions,
        activeOwnerGroupOptions,

        // Current selections
        selectedYear,
        selectedPeriod,
        selectedOwner,
        selectedGroup,
        selectedApartment,
        aggregateMethod,

        // Loading states
        isLoading: {
            owners: false,
            apartments: false,
            groups: false,
            expenseCategories: false,
            years: false,
        },
    };
};
